import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { db } from '../config/firebase';
import { collection, addDoc, doc, setDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const AdminPartnerRegistrationPage = ({ darkMode }) => {
  const [formData, setFormData] = useState({
    partnerName: '',
    phoneNumber: '',
    email: '',
    institutionName: '',
    address: '',
    paymentAccount: {
      hasUpi: false,
      hasBank: false,
      upiId: '',
      bankAccount: {
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        accountHolderName: ''
      }
    }
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [checkingUniqueness, setCheckingUniqueness] = useState({
    phone: false,
    email: false
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Function to generate next OV ID
  const generateNextOVID = async () => {
    try {
      const partnersRef = collection(db, 'partners');
      const querySnapshot = await getDocs(partnersRef);
      
      let maxOVNumber = 0; // Start from 0 to automatically detect the highest existing OV ID
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.partnerID && data.partnerID.startsWith('OV')) {
          const numberPart = parseInt(data.partnerID.substring(2));
          if (!isNaN(numberPart) && numberPart > maxOVNumber) {
            maxOVNumber = numberPart;
          }
        }
      });
      
      // If no OV IDs exist, start from OV01, otherwise increment the highest found
      const nextNumber = maxOVNumber + 1;
      return `OV${nextNumber.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error generating OV ID:', error);
      // Fallback to timestamp-based ID if there's an error
      const timestamp = Date.now().toString();
      return `OV${timestamp.slice(-2)}`;
    }
  };

  // Validation functions
  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }
    if (!cleanPhone.startsWith('6') && !cleanPhone.startsWith('7') && !cleanPhone.startsWith('8') && !cleanPhone.startsWith('9')) {
      return 'Please enter a valid Indian mobile number';
    }
    return '';
  };

  // Debounced uniqueness checking functions
  const checkPhoneUniqueness = useCallback(async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length !== 10) return;
    
    setCheckingUniqueness(prev => ({ ...prev, phone: true }));
    try {
      const partnersRef = collection(db, 'partners');
      const q = query(partnersRef, where('phoneNumber', '==', phoneNumber));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        setValidationErrors(prev => ({ 
          ...prev, 
          phoneNumber: 'This phone number is already registered' 
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phoneNumber;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error checking phone uniqueness:', error);
    } finally {
      setCheckingUniqueness(prev => ({ ...prev, phone: false }));
    }
  }, []);

  const checkEmailUniqueness = useCallback(async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    
    setCheckingUniqueness(prev => ({ ...prev, email: true }));
    try {
      const partnersRef = collection(db, 'partners');
      const q = query(partnersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        setValidationErrors(prev => ({ 
          ...prev, 
          email: 'This email is already registered' 
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error checking email uniqueness:', error);
    } finally {
      setCheckingUniqueness(prev => ({ ...prev, email: false }));
    }
  }, []);

  // Debounce utility
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const debouncedPhoneCheck = useCallback(debounce(checkPhoneUniqueness, 500), [checkPhoneUniqueness]);
  const debouncedEmailCheck = useCallback(debounce(checkEmailUniqueness, 500), [checkEmailUniqueness]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.partnerName.trim()) {
      errors.partnerName = 'Partner name is required';
    }
    
    const phoneError = validatePhoneNumber(formData.phoneNumber);
    if (phoneError) {
      errors.phoneNumber = phoneError;
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.address.trim()) {
      errors.address = 'Address/Place is required';
    }

    // No validation for payment details since they are optional
    // Users can provide UPI, Bank details, both, or neither

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle payment account fields
    if (name.startsWith('bankAccount.')) {
      const fieldName = name.replace('bankAccount.', '');
      setFormData(prev => ({
        ...prev,
        paymentAccount: {
          ...prev.paymentAccount,
          bankAccount: {
            ...prev.paymentAccount.bankAccount,
            [fieldName]: value
          }
        }
      }));
      return;
    }
    
    if (name.startsWith('paymentAccount.')) {
      const fieldName = name.replace('paymentAccount.', '');
      setFormData(prev => ({
        ...prev,
        paymentAccount: {
          ...prev.paymentAccount,
          [fieldName]: value
        }
      }));
      return;
    }
    
    // Handle phone number formatting and validation
    if (name === 'phoneNumber') {
      let cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length > 10) {
        cleanValue = cleanValue.slice(0, 10);
      }
      setFormData({ ...formData, [name]: cleanValue });
      
      // Real-time validation for phone number
      const phoneError = validatePhoneNumber(cleanValue);
      if (phoneError) {
        setValidationErrors(prev => ({ ...prev, phoneNumber: phoneError }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phoneNumber;
          return newErrors;
        });
        
        // Check uniqueness only if the phone number is valid (10 digits)
        if (cleanValue.length === 10) {
          debouncedPhoneCheck(cleanValue);
        }
      }
      return;
    }
    
    // Handle email validation
    if (name === 'email') {
      const emailValue = value.trim().toLowerCase();
      setFormData({ ...formData, [name]: emailValue });
      
      // Real-time validation for email
      if (!emailValue) {
        setValidationErrors(prev => ({ ...prev, email: 'Email address is required' }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        setValidationErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
        
        // Check uniqueness only if the email is valid
        debouncedEmailCheck(emailValue);
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
    
    // Clear validation error when user types in other fields
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors below');
      return;
    }

    // Check if there are any validation errors (including uniqueness errors)
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    // Check if uniqueness validation is still in progress
    if (checkingUniqueness.phone || checkingUniqueness.email) {
      setError('Please wait for validation to complete');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate OV ID
      const partnerID = await generateNextOVID();
      
      // Determine which payment methods are provided based on field values
      const hasUpi = formData.paymentAccount.upiId.trim() !== '';
      const hasBankAccount = formData.paymentAccount.bankAccount.accountNumber.trim() !== '' ||
                             formData.paymentAccount.bankAccount.ifscCode.trim() !== '' ||
                             formData.paymentAccount.bankAccount.bankName.trim() !== '' ||
                             formData.paymentAccount.bankAccount.accountHolderName.trim() !== '';
      
      // Prepare payment account data
      const paymentAccount = {
        hasUpi: hasUpi,
        hasBank: hasBankAccount,
        upiId: hasUpi ? formData.paymentAccount.upiId.trim() : '',
        bankAccount: hasBankAccount ? formData.paymentAccount.bankAccount : {
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          accountHolderName: ''
        }
      };

      // Only save payment account if at least one method is provided
      const hasPaymentInfo = hasUpi || hasBankAccount;
      
      const registrationData = {
        partnerName: formData.partnerName.trim(),
        phoneNumber: formData.phoneNumber,
        partnerID: partnerID, // Add the generated OV ID
        email: formData.email.trim().toLowerCase(),
        institutionName: formData.institutionName.trim() || null,
        address: formData.address.trim(),
        password: `${partnerID}@OV2025`, // OV ID password
        phonePassword: `${formData.phoneNumber}@OV2025`, // Phone number password
        registeredAt: serverTimestamp(),
        registeredBy: 'admin',
        status: 'active',
        totalReferrals: 0,
        activeReferrals: 0,
        paymentAccount: hasPaymentInfo ? paymentAccount : null
      };

      console.log('Registering partner:', registrationData);

      // Store in Firebase Firestore
      await setDoc(doc(db, 'partners', formData.phoneNumber), registrationData);
      
      // Also create an empty referrals subcollection for this partner
      const referralsRef = collection(db, 'partners', formData.phoneNumber, 'referrals');
      // The collection will be created when the first referral is added
      
      console.log('Partner successfully registered in Firebase!');
      
      setSuccess(`Partner registered successfully! 
        
        Login Options:
        1. Traditional Login (Two ways):
           Option A: Partner ID: ${partnerID} | Password: ${partnerID}@OV2025
           Option B: Partner ID: ${formData.phoneNumber} | Password: ${formData.phoneNumber}@OV2025
        
        2. Google Sign-in:
           Email: ${formData.email}
           (Can sign in directly with Google using this email)
        
        Please share these credentials securely with the partner.
        Note: Partners can use EITHER their OV ID (${partnerID}) OR phone number (${formData.phoneNumber}) to login.
        
        ✅ Partner has been saved to Firebase and will appear in Partners Management.
        ✅ You can add students to this partner from the management page.
        ✅ Data persists across page refreshes and browser sessions.
        ✅ Partner can use either login method for convenient access.`);
      
      // Reset form
      setFormData({
        partnerName: '',
        phoneNumber: '',
        email: '',
        institutionName: '',
        address: '',
        paymentAccount: {
          hasUpi: false,
          hasBank: false,
          upiId: '',
          bankAccount: {
            accountNumber: '',
            ifscCode: '',
            bankName: '',
            accountHolderName: ''
          }
        }
      });
      
    } catch (err) {
      console.error('Partner registration error:', err);
      setError(`Failed to register partner: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin-dashboard'); // Navigate back to admin dashboard
  };

  // Icon components
  const UserIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const PhoneIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );

  const EmailIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  const BuildingIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );

  const LocationIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <div className={`min-h-screen relative overflow-hidden ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
    }`}>
      
      {/* Apple-style animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-3xl">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)`
          }}></div>
        </div>
      </div>

      <div className="relative z-10 min-h-screen py-2 sm:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto w-full">
          
          {/* Back button */}
          <button
            onClick={handleGoBack}
            className={`mb-2 sm:mb-4 inline-flex items-center px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base ${
              darkMode 
                ? 'bg-white/10 text-white hover:bg-white/20 border border-white/20' 
                : 'bg-white/50 text-gray-700 hover:bg-white/70 border border-white/30'
            } backdrop-blur-md shadow-lg`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>

          {/* Main container */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-6 md:p-8 relative overflow-hidden w-full max-w-full">
            
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent rounded-2xl sm:rounded-3xl"></div>
            
            <div className="relative space-y-3 sm:space-y-6 max-w-full overflow-hidden">
              
              {/* Header */}
              <div className="text-center space-y-2 sm:space-y-3">
                <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-green-500/20 to-blue-500/20 backdrop-blur-md border border-white/30 flex items-center justify-center mb-2 sm:mb-4 shadow-lg">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                
                <div className="space-y-1 sm:space-y-2">
                  <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Partner Registration
                  </h1>
                  <p className={`text-sm sm:text-base md:text-lg font-medium ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
                    Register New Referral Partner
                  </p>
                  <p className={`text-xs sm:text-sm px-2 sm:px-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Add coaching centers, institutions, or individuals as referral partners
                  </p>
                </div>

                {/* Partners Refers Button */}
                <div className="pt-2 sm:pt-3">
                  <button
                    onClick={() => navigate('/admin/partners-management')}
                    className={`inline-flex items-center px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 hover:scale-105 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-300`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="whitespace-nowrap">Partners Refers</span>
                  </button>
                </div>
              </div>

              {/* Success Message */}
              {success && (
                <div className="backdrop-blur-md bg-green-500/20 border border-green-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <SuccessIcon />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-black font-semibold mb-2 text-sm sm:text-base">Registration Successful!</h3>
                      <pre className="text-black text-xs sm:text-sm whitespace-pre-wrap font-mono bg-green-900/20 p-2 sm:p-3 rounded-lg overflow-x-auto break-words max-w-full">
                        {success}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="backdrop-blur-md bg-red-500/20 border border-red-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <p className="text-black text-xs sm:text-sm text-center font-medium break-words">{error}</p>
                </div>
              )}

              {/* Registration Form */}
              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
                
                {/* Partner Name */}
                <div className="space-y-2">
                  <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <UserIcon className="inline mr-2" />
                    Partner Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="partnerName"
                      value={formData.partnerName}
                      onChange={handleChange}
                      placeholder="Enter partner's full name"
                      className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 ${
                        validationErrors.partnerName 
                          ? 'border-red-500/50 focus:border-red-500/70' 
                          : 'border-white/20 focus:border-white/40'
                      } ${
                        darkMode 
                          ? 'text-white placeholder-gray-300' 
                          : 'text-gray-900 placeholder-gray-500'
                      } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                      required
                    />
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  {validationErrors.partnerName && (
                    <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.partnerName}</p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <PhoneIcon className="inline mr-2" />
                    <span className="break-words">Phone Number * (This will be their Partner ID)</span>
                  </label>
                  <div className="relative">
                    <div className="flex">
                      <span className="inline-flex items-center px-3 sm:px-4 text-xs sm:text-sm border border-r-0 border-white/20 rounded-l-xl sm:rounded-l-2xl backdrop-blur-md bg-white/10 text-gray-300 flex-shrink-0">
                        +91
                      </span>
                      <div className="relative flex-1">
                        <input
                          type="text"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          placeholder="Enter 10-digit mobile number"
                          maxLength="10"
                          className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-r-xl sm:rounded-r-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 ${
                            validationErrors.phoneNumber 
                              ? 'border-red-500/70 focus:border-red-500/90 bg-red-50/10' 
                              : checkingUniqueness.phone
                              ? 'border-yellow-500/50 focus:border-yellow-500/70'
                              : 'border-white/20 focus:border-white/40'
                          } ${
                            darkMode 
                              ? 'text-white placeholder-gray-300' 
                              : 'text-gray-900 placeholder-gray-500'
                          } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                          required
                        />
                        {checkingUniqueness.phone && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  {validationErrors.phoneNumber && (
                    <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.phoneNumber}</p>
                  )}
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} break-words`}>
                    This number will be used as Partner ID for login
                  </p>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <EmailIcon className="inline mr-2" />
                    Email Address *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="partner@example.com"
                      className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 ${
                        validationErrors.email 
                          ? 'border-red-500/70 focus:border-red-500/90 bg-red-50/10' 
                          : checkingUniqueness.email
                          ? 'border-yellow-500/50 focus:border-yellow-500/70'
                          : 'border-white/20 focus:border-white/40'
                      } ${
                        darkMode 
                          ? 'text-white placeholder-gray-300' 
                          : 'text-gray-900 placeholder-gray-500'
                      } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                      required
                    />
                    {checkingUniqueness.email && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                      </div>
                    )}
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  {validationErrors.email && (
                    <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.email}</p>
                  )}
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Required - for Google Sign-in access and communication
                  </p>
                </div>

                {/* Institution Name */}
                <div className="space-y-2">
                  <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <BuildingIcon className="inline mr-2" />
                    <span className="break-words">Institution/Coaching Center Name (Optional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleChange}
                      placeholder="e.g., ABC Coaching Center (leave blank for individual referrers)"
                      className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 transition-all duration-300 ${
                        darkMode 
                          ? 'text-white placeholder-gray-300' 
                          : 'text-gray-900 placeholder-gray-500'
                      } focus:bg-white/20 focus:border-white/40 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                    />
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Optional - for tracking purposes only
                  </p>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <LocationIcon className="inline mr-2" />
                    Address/Place *
                  </label>
                  <div className="relative">
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter address, city, or location"
                      rows="3"
                      className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 resize-none ${
                        validationErrors.address 
                          ? 'border-red-500/50 focus:border-red-500/70' 
                          : 'border-white/20 focus:border-white/40'
                      } ${
                        darkMode 
                          ? 'text-white placeholder-gray-300' 
                          : 'text-gray-900 placeholder-gray-500'
                      } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                      required
                    />
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  {validationErrors.address && (
                    <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.address}</p>
                  )}
                </div>

                {/* Payment Account Details (Optional) */}
                <div className="space-y-4 border-t border-white/20 pt-4">
                  <h3 className={`text-sm sm:text-base font-semibold flex items-center gap-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Payment Account Details (Optional)
                  </h3>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Commission payments will be sent to this account after successful referrals
                  </p>
                  
                  {/* UPI Payment Details */}
                  <div className="space-y-2">
                    <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      UPI Payment Details
                    </h4>
                    <div className="space-y-2">
                      <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        UPI ID
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="paymentAccount.upiId"
                          value={formData.paymentAccount.upiId}
                          onChange={handleChange}
                          placeholder="e.g., user@paytm"
                          className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 ${
                            validationErrors.upiId 
                              ? 'border-red-500/50 focus:border-red-500/70' 
                              : 'border-white/20 focus:border-white/40'
                          } ${
                            darkMode 
                              ? 'text-white placeholder-gray-300' 
                              : 'text-gray-900 placeholder-gray-500'
                          } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                        />
                        <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                      </div>
                      {validationErrors.upiId && (
                        <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.upiId}</p>
                      )}
                    </div>
                  </div>

                  {/* Bank Account Details */}
                  <div className="space-y-4">
                    <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Bank Account Details
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Account Holder Name
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="bankAccount.accountHolderName"
                              value={formData.paymentAccount.bankAccount.accountHolderName}
                              onChange={handleChange}
                              placeholder="Enter account holder name"
                              className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border transition-all duration-300 ${
                                validationErrors.accountHolderName 
                                  ? 'border-red-500/50 focus:border-red-500/70' 
                                  : 'border-white/20 focus:border-white/40'
                              } ${
                                darkMode 
                                  ? 'text-white placeholder-gray-300' 
                                  : 'text-gray-900 placeholder-gray-500'
                              } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                            />
                            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                          </div>
                          {validationErrors.accountHolderName && (
                            <p className="text-red-400 text-xs sm:text-sm break-words">{validationErrors.accountHolderName}</p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Account Number
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="bankAccount.accountNumber"
                              value={formData.paymentAccount.bankAccount.accountNumber}
                              onChange={handleChange}
                              placeholder="Enter account number"
                              className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 focus:border-white/40 ${
                                darkMode 
                                  ? 'text-white placeholder-gray-300' 
                                  : 'text-gray-900 placeholder-gray-500'
                              } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg transition-all duration-300`}
                            />
                            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            IFSC Code
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="bankAccount.ifscCode"
                              value={formData.paymentAccount.bankAccount.ifscCode}
                              onChange={handleChange}
                              placeholder="Enter IFSC code"
                              className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 focus:border-white/40 ${
                                darkMode 
                                  ? 'text-white placeholder-gray-300' 
                                  : 'text-gray-900 placeholder-gray-500'
                              } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg transition-all duration-300`}
                            />
                            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <label className={`block text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Bank Name
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="bankAccount.bankName"
                              value={formData.paymentAccount.bankAccount.bankName}
                              onChange={handleChange}
                              placeholder="Enter bank name"
                              className={`w-full p-3 sm:p-4 text-sm sm:text-base rounded-xl sm:rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 focus:border-white/40 ${
                                darkMode 
                                  ? 'text-white placeholder-gray-300' 
                                  : 'text-gray-900 placeholder-gray-500'
                              } focus:bg-white/20 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg transition-all duration-300`}
                            />
                            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl font-semibold text-white transition-all duration-300 transform ${
                    loading
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:scale-105 active:scale-95'
                  } bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 shadow-2xl hover:shadow-green-500/25 overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-xl sm:rounded-2xl"></div>
                  
                  <div className="relative flex items-center justify-center space-x-2 sm:space-x-3">
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-base sm:text-lg">Register Partner</span>
                      </>
                    )}
                  </div>
                  
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-pulse"></div>
                  </div>
                </button>
              </form>

              {/* Registration Info */}
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 space-y-3">
                <h3 className={`text-base sm:text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Partner Login Options:
                </h3>
                <div className={`space-y-2 text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} break-words`}>
                  <div className="border-l-4 border-blue-500 pl-3">
                    <p><strong>Option 1 - Traditional Login (Two Ways):</strong></p>
                    <p><strong>Way A:</strong> OV ID (next: OV05) + Password: OVID@OV2025</p>
                    <p><strong>Way B:</strong> Phone Number + Password: PhoneNumber@OV2025</p>
                    <p className="text-xs text-gray-500 mt-1">Note: Legacy partners (existing before OV system) will continue using phone number as ID</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-3 mt-3">
                    <p><strong>Option 2 - Google Sign-in:</strong></p>
                    <p><strong>Email:</strong> {formData.email || 'Registered email address'}</p>
                    <p>Partners can sign in directly with their Google account</p>
                  </div>
                  <p className="text-xs mt-3"><strong>Registration Time:</strong> {currentTime.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default AdminPartnerRegistrationPage;
